import React, { useEffect, useState } from 'react'
import { Button, CircularProgress, TextField, Typography } from '@mui/material'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
} from 'components/ui'
import { useUserAuth } from 'contexts/google-auth'
import { useRecordEscalationReason } from 'services/queries/use-record-escalation-reason'
import { EscalationListItem } from 'types'
import { countryLabel } from './escalations.helpers'

type Props = {
  /** The row being edited, or null when the dialog is closed. */
  escalation: EscalationListItem | null
  onClose: () => void
}

/**
 * Records (or edits) the reason a company's financials are missing.
 *
 * This used to be a textarea pinned under the table, which only worked because
 * the page could show exactly one row. On a list it has to belong to a row, so
 * it moved into a dialog opened from that row.
 */
const RecordReasonDialog: React.FC<Props> = ({ escalation, onClose }) => {
  const { user } = useUserAuth()
  const [reason, setReason] = useState('')
  const recordReasonMutation = useRecordEscalationReason()

  // Re-seed whenever a different row is opened, so the dialog never shows the
  // previous row's text.
  useEffect(() => {
    setReason(escalation?.reason ?? '')
  }, [escalation])

  if (!escalation) return null

  const trimmed = reason.trim()
  const unchanged = trimmed === (escalation.reason ?? '').trim()
  const canSave = !!trimmed && !unchanged && !recordReasonMutation.isLoading

  const handleSave = () => {
    if (!canSave) return
    recordReasonMutation.mutate(
      {
        country: escalation.country,
        payload: {
          local_id: escalation.local_id,
          reason: trimmed,
          // Advisory only: CustomerManagementAPI overrides this with the
          // authenticated operator's address. Still sent because RisikaAPI's
          // schema requires the field and CMAPI falls back to it.
          reason_recorded_by: user?.email ?? '',
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open onClose={onClose} size="sm">
      <DialogHeader
        title={escalation.reason ? 'Edit reason' : 'Record reason'}
        subtitle={`${
          escalation.company_name ?? escalation.local_id
        } · ${countryLabel(escalation.country)}`}
        showClose
        onClose={onClose}
      />
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
          Visible to every customer looking at this company.
        </Typography>
        <TextField
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          rows={4}
          fullWidth
          autoFocus
          variant="outlined"
          placeholder="Why are the financials missing?"
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }}
          data-cy="escalation-reason-input"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={recordReasonMutation.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          data-cy="escalation-reason-save"
        >
          {recordReasonMutation.isLoading ? (
            <CircularProgress size={20} />
          ) : (
            'Save reason'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecordReasonDialog
